/**
 * CorporateEvaluationPanel — CEO Big-Picture Scorecard
 *
 * Renders the full NECS platform evaluation in a single scroll:
 * live Corporate Snapshot, side-by-side "This Month" vs "Last 30 Days"
 * KPIs for Finance / Sales / Field Ops / Risk / HR / Legal, plus a
 * traffic-light Department Health Grid so the CEO knows within one
 * glance where fires are burning.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DollarSign, TrendingUp, Truck, Shield, Users, Scale, Building2,
  FileText, AlertTriangle, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : n ?? '—');
const usd = (n) => (typeof n === 'number'
  ? '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  : '$—');

/**
 * Sparkline — tiny inline SVG showing a 6-month trend.
 * Renders a smooth path + trailing dot. Auto-normalizes to [0, max].
 */
function Sparkline({ points = [], color = '#FFD60A', width = 84, height = 22 }) {
  if (!points || points.length < 2) {
    return <span className="text-zinc-600 text-[10px]">—</span>;
  }
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = max - min || 1;
  const step = points.length > 1 ? w / (points.length - 1) : 0;
  const coords = points.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (h - ((v - min) / range) * h);
    return [x, y];
  });
  const d = coords.reduce((acc, [x, y], i) => acc + (i === 0 ? `M${x},${y}` : ` L${x},${y}`), '');
  const [lx, ly] = coords[coords.length - 1];
  const [fx] = coords[0];
  const fillPath = d + ` L${lx},${height - pad} L${fx},${height - pad} Z`;
  const trend = points[points.length - 1] - points[0];
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <path d={fillPath} fill={color} opacity={0.12} />
      <path d={d} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r={2.2} fill={color} />
      <title>{`6mo trend: ${trend >= 0 ? '+' : ''}${trend.toFixed(0)}`}</title>
    </svg>
  );
}

function ComparisonRow({ label, month, rolling, valueFmt = fmt, accent = '#FFD60A', spark = null }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
      <span className="text-sm text-zinc-300">{label}</span>
      <div className="flex items-center gap-3 tabular-nums">
        {spark && <Sparkline points={spark} color={accent} />}
        <span className="text-white text-sm font-semibold min-w-[80px] text-right">{valueFmt(month)}</span>
        <span className="text-zinc-500 text-xs w-4 text-center">·</span>
        <span className="text-sm font-semibold min-w-[80px] text-right" style={{ color: accent }}>
          {valueFmt(rolling)}
        </span>
      </div>
    </div>
  );
}

function PanelCard({ title, icon: Icon, accent, children, testid }) {
  return (
    <Card
      className="p-5 bg-[#111114] border border-white/10 rounded-2xl"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="h-8 w-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: accent + '22' }}
        >
          <Icon className="h-4 w-4" style={{ color: accent }} />
        </div>
        <h4 className="text-white font-bold tracking-wide text-sm uppercase">{title}</h4>
      </div>
      <div className="text-[10px] uppercase text-zinc-500 flex items-center justify-end gap-3 pb-1 mb-1 border-b border-white/10">
        <span className="w-[84px] text-right">6mo trend</span>
        <span className="min-w-[80px] text-right">This Month</span>
        <span className="w-4 text-center">·</span>
        <span className="min-w-[80px] text-right" style={{ color: accent }}>30 Days</span>
      </div>
      {children}
    </Card>
  );
}

const STATUS_COLORS = {
  green: { bg: '#052e1a', text: '#22c55e', label: 'Healthy' },
  amber: { bg: '#3a2a06', text: '#F59E0B', label: 'Watch' },
  red:   { bg: '#3a0d0d', text: '#EF4444', label: 'Critical' },
};

function DeptHealthGrid({ items }) {
  if (!items?.length) return null;
  return (
    <Card className="p-5 bg-[#111114] border border-white/10 rounded-2xl">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-[#FFD60A22] flex items-center justify-center">
          <Building2 className="h-4 w-4 text-[#FFD60A]" />
        </div>
        <h4 className="text-white font-bold tracking-wide text-sm uppercase">
          Department Health · Live
        </h4>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((d) => {
          const c = STATUS_COLORS[d.status] || STATUS_COLORS.green;
          return (
            <div
              key={d.key}
              className="rounded-xl p-3 border"
              style={{ backgroundColor: c.bg, borderColor: c.text + '33' }}
              data-testid={`dept-health-${d.key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-white text-xs font-bold uppercase tracking-wide">{d.label}</span>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.text }} />
              </div>
              <div className="text-[11px] font-semibold" style={{ color: c.text }}>{c.label}</div>
              <div className="text-[10px] text-zinc-400 mt-1">{d.detail}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function ComplianceFeed() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ title: '', body: '', category: 'regulatory', jurisdiction: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/compliance-log?limit=25`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setEntries(j.entries || []);
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!draft.title.trim() || !draft.body.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/compliance-log`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (r.ok) {
        setDraft({ title: '', body: '', category: 'regulatory', jurisdiction: '' });
        setOpen(false);
        load();
      }
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-[#111114] border border-white/10 rounded-2xl" data-testid="compliance-feed">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#8B5CF622] flex items-center justify-center">
            <Scale className="h-4 w-4 text-[#8B5CF6]" />
          </div>
          <h4 className="text-white font-bold tracking-wide text-sm uppercase">
            Legal &amp; Compliance Log
          </h4>
        </div>
        <Button
          size="sm"
          onClick={() => setOpen((v) => !v)}
          className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-xs h-8"
          data-testid="compliance-log-toggle"
        >
          {open ? 'Cancel' : '+ Add Entry'}
        </Button>
      </div>

      {open && (
        <div className="space-y-2 mb-4 p-3 bg-black/40 rounded-lg border border-white/10">
          <input
            className="w-full bg-transparent border border-white/15 rounded px-2 py-1.5 text-sm text-white"
            placeholder="Title (e.g. Insurance renewal — GL policy)"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            data-testid="compliance-title-input"
          />
          <textarea
            className="w-full bg-transparent border border-white/15 rounded px-2 py-1.5 text-sm text-white min-h-[70px]"
            placeholder="Details / action taken / next steps"
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            data-testid="compliance-body-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              className="bg-transparent border border-white/15 rounded px-2 py-1.5 text-sm text-white"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              data-testid="compliance-category-select"
            >
              {['policy', 'license', 'insurance', 'audit', 'regulatory', 'incident', 'other'].map((c) => (
                <option key={c} value={c} className="bg-black">{c}</option>
              ))}
            </select>
            <input
              className="bg-transparent border border-white/15 rounded px-2 py-1.5 text-sm text-white"
              placeholder="Jurisdiction (optional)"
              value={draft.jurisdiction}
              onChange={(e) => setDraft({ ...draft, jurisdiction: e.target.value })}
            />
          </div>
          <div className="text-right">
            <Button size="sm" onClick={submit} disabled={busy}
                    className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
                    data-testid="compliance-submit-btn">
              {busy ? 'Saving…' : 'Log Entry'}
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-zinc-500 text-xs py-4 text-center">No compliance entries yet. Click &quot;+ Add Entry&quot; to log the first one.</div>
      ) : (
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
          {entries.map((e) => (
            <div key={e.entry_id} className="p-3 bg-black/30 rounded-lg border border-white/5"
                 data-testid={`compliance-entry-${e.entry_id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-white text-sm font-semibold">{e.title}</div>
                <Badge className="text-[10px] bg-[#8B5CF622] text-[#C4B5FD] border-0">{e.category}</Badge>
              </div>
              <div className="text-zinc-400 text-xs mb-1">{e.body}</div>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span>{e.logged_by_name}</span>
                {e.jurisdiction && <span>· {e.jurisdiction}</span>}
                <span>· {new Date(e.created_at).toLocaleDateString()}</span>
                <span className={
                  e.status === 'resolved' ? 'text-emerald-400'
                    : e.status === 'in-review' ? 'text-amber-400' : 'text-orange-400'
                }>· {e.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function CorporateEvaluationPanel() {
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [rScore, rTrends] = await Promise.all([
        fetch(`${API}/api/executive/scorecard`, { credentials: 'include' }),
        fetch(`${API}/api/executive/scorecard/trends?months=6`, { credentials: 'include' }),
      ]);
      if (rScore.ok) setData(await rScore.json());
      if (rTrends.ok) setTrends(await rTrends.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <Card className="p-6 bg-[#0a0a0b] border border-white/10 rounded-2xl">
        <div className="text-zinc-400 text-sm">Loading Corporate Evaluation…</div>
      </Card>
    );
  }
  if (!data) return null;

  const { corporate_snapshot: snap, this_month: m, last_30d: r, department_health: health, period_labels } = data;
  const series = trends?.series || {};
  const spark = (k) => series[k] || null;

  return (
    <div className="space-y-4" data-testid="corporate-evaluation-panel">
      {/* Header */}
      <Card className="p-5 bg-gradient-to-br from-[#0a0a0b] to-[#1a1a1e] border border-[#FFD60A]/30 rounded-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[#FFD60A] text-[11px] font-bold uppercase tracking-widest mb-1">
              Corporate Evaluation · NECS Platform
            </div>
            <h3 className="text-white text-2xl font-black tracking-tight">
              Big Picture — {period_labels.month} vs Last 30 Days
            </h3>
            <div className="text-zinc-400 text-xs mt-1">
              Generated {new Date(data.generated_at).toLocaleString()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={load}
                    className="text-zinc-300 hover:text-white hover:bg-white/10"
                    data-testid="corp-eval-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" variant="ghost"
                    onClick={() => setExpanded((v) => !v)}
                    className="text-zinc-300 hover:text-white hover:bg-white/10"
                    data-testid="corp-eval-collapse-btn">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Corporate snapshot strip — always-visible headline totals */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            ['Properties', snap.properties],
            ['Active Contracts', snap.active_contracts],
            ['Employees', snap.employees_total],
            ['Property Managers', snap.property_managers],
            ['Tenants', snap.tenants],
            ['Regions', snap.regions],
          ].map(([label, value]) => (
            <div key={label} className="bg-black/40 rounded-lg p-3 border border-white/5">
              <div className="text-zinc-500 text-[10px] uppercase tracking-wide">{label}</div>
              <div className="text-white text-2xl font-black tabular-nums">{fmt(value)}</div>
            </div>
          ))}
        </div>
      </Card>

      {expanded && (
        <>
          {/* KPI grid — 2 columns on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PanelCard title="Finance" icon={DollarSign} accent="#22c55e" testid="scorecard-finance">
              <ComparisonRow label="Revenue billed" month={m.finance.revenue_billed} rolling={r.finance.revenue_billed} valueFmt={usd} accent="#22c55e" spark={spark('revenue_billed')} />
              <ComparisonRow label="Revenue paid" month={m.finance.revenue_paid} rolling={r.finance.revenue_paid} valueFmt={usd} accent="#22c55e" spark={spark('revenue_paid')} />
              <ComparisonRow label="Receivables outstanding" month={m.finance.receivables_outstanding} rolling={r.finance.receivables_outstanding} valueFmt={usd} accent="#22c55e" />
              <ComparisonRow label="Invoices created" month={m.finance.invoices_created} rolling={r.finance.invoices_created} accent="#22c55e" spark={spark('invoices_created')} />
            </PanelCard>

            <PanelCard title="Sales &amp; Contracts" icon={TrendingUp} accent="#3B82F6" testid="scorecard-sales">
              <ComparisonRow label="New contracts" month={m.sales.new_contracts} rolling={r.sales.new_contracts} accent="#3B82F6" spark={spark('new_contracts')} />
              <ComparisonRow label="Lost contracts" month={m.sales.lost_contracts} rolling={r.sales.lost_contracts} accent="#3B82F6" spark={spark('lost_contracts')} />
              <ComparisonRow label="Active contracts (total)" month={m.sales.active_contracts} rolling={r.sales.active_contracts} accent="#3B82F6" />
              <ComparisonRow label="Website inquiries" month={m.sales.inquiries} rolling={r.sales.inquiries} accent="#3B82F6" spark={spark('inquiries')} />
            </PanelCard>

            <PanelCard title="Field Operations" icon={Truck} accent="#FFD60A" testid="scorecard-field-ops">
              <ComparisonRow label="Pickups logged" month={m.field_ops.pickups} rolling={r.field_ops.pickups} accent="#FFD60A" spark={spark('pickups')} />
              <ComparisonRow label="Daily reports" month={m.field_ops.daily_reports} rolling={r.field_ops.daily_reports} accent="#FFD60A" />
              <ComparisonRow label="Service requests" month={m.field_ops.service_requests} rolling={r.field_ops.service_requests} accent="#FFD60A" spark={spark('service_requests')} />
              <ComparisonRow label="Active crews now" month={m.field_ops.active_crews_now} rolling={r.field_ops.active_crews_now} accent="#FFD60A" />
            </PanelCard>

            <PanelCard title="Risk &amp; Safety" icon={Shield} accent="#EF4444" testid="scorecard-risk">
              <ComparisonRow label="Safety incidents" month={m.risk.incidents} rolling={r.risk.incidents} accent="#EF4444" spark={spark('incidents')} />
              <ComparisonRow label="Violations logged" month={m.risk.violations_total} rolling={r.risk.violations_total} accent="#EF4444" spark={spark('violations_total')} />
              <ComparisonRow label="Tenant disputes" month={m.risk.disputes} rolling={r.risk.disputes} accent="#EF4444" spark={spark('disputes')} />
              <ComparisonRow label="Vehicle safety checks" month={m.risk.safety_checks} rolling={r.risk.safety_checks} accent="#EF4444" />
            </PanelCard>

            <PanelCard title="Human Resources" icon={Users} accent="#EC4899" testid="scorecard-hr">
              <ComparisonRow label="New hires" month={m.hr.new_hires} rolling={r.hr.new_hires} accent="#EC4899" spark={spark('new_hires')} />
              <ComparisonRow label="Terminations" month={m.hr.terminations} rolling={r.hr.terminations} accent="#EC4899" spark={spark('terminations')} />
            </PanelCard>

            <PanelCard title="Legal &amp; Compliance" icon={FileText} accent="#8B5CF6" testid="scorecard-legal">
              <ComparisonRow label="Compliance log entries" month={m.legal.compliance_updates} rolling={r.legal.compliance_updates} accent="#8B5CF6" spark={spark('compliance_updates')} />
              <div className="text-[11px] text-zinc-500 pt-2">
                See the full log below to add or track legal / regulatory updates.
              </div>
            </PanelCard>
          </div>

          {/* Department Health Grid */}
          <DeptHealthGrid items={health} />

          {/* Compliance feed */}
          <ComplianceFeed />
        </>
      )}
    </div>
  );
}
